/* ============================================================================
   Email Gate — custom element for sections/email-gate.liquid.

   Reads its config from data-* attributes, gates the page until a valid email
   is submitted to Klaviyo, then sets a persistent cookie. Every gate sharing
   the same cookie name unlocks together (cookie is path=/).

   Design notes:
   - The section's inline pre-render script has already set the <html> state
     class + handled the magic-link bypass before this runs. This element
     reconciles state, wires the form, and manages focus / inert for a11y.
   - Debug logging is OFF by default. Enable with Shopify.designMode, or by
     adding ?email_gate_debug=1 to the URL.
   ========================================================================== */
(function () {
  'use strict';

  var DEBUG = false;
  try {
    DEBUG =
      (window.Shopify && window.Shopify.designMode) ||
      new URLSearchParams(window.location.search).get('email_gate_debug') === '1';
  } catch (_) {}

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[email-gate]'].concat([].slice.call(arguments))); } catch (_) {}
  }

  var ACTIVE = 'email-gate-active';
  var UNLOCKED = 'email-gate-unlocked';

  class EmailGate extends HTMLElement {
    connectedCallback() {
      // Belt-and-braces teleport (the section's inline script normally does this
      // first). Reparenting re-fires connectedCallback; the guard prevents the
      // second pass from re-running setup.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      // Theme editor: never block the merchandiser. Clear any pre-render blur.
      if (window.Shopify && window.Shopify.designMode) {
        this._clearBackground();
        document.documentElement.classList.remove(ACTIVE);
        this.hidden = true;
        return;
      }

      this.cookieName = this.dataset.cookieName || 'gt_gate_unlocked';

      // Already unlocked (cookie set post-render in this same tab, or the
      // pre-render script missed it): reconcile and bail.
      if (this._getCookie(this.cookieName)) {
        this._markUnlocked();
        return;
      }

      this.form = this.querySelector('.email-gate__form');
      this.input = this.querySelector('.email-gate__input');
      this.button = this.querySelector('.email-gate__button');
      this.error = this.querySelector('.email-gate__error');
      this.success = this.querySelector('.email-gate__success');

      if (this.form) {
        this._onSubmit = this._onSubmit.bind(this);
        this.form.addEventListener('submit', this._onSubmit);
      }

      // A11y: make everything behind the gate inert (keyboard + assistive tech
      // cannot reach the blurred content), then move focus into the panel.
      this._setBackgroundInert(true);
      this._focusInput();
    }

    disconnectedCallback() {
      // Defensive cleanup so a removed gate never leaves the page blurred/locked.
      document.documentElement.classList.remove(ACTIVE);
      this._clearBackground();
    }

    /* ---- background inert management -------------------------------------- */

    _backgroundNodes() {
      var nodes = [];
      var children = document.body ? document.body.children : [];
      for (var i = 0; i < children.length; i++) {
        if (children[i] !== this) nodes.push(children[i]);
      }
      return nodes;
    }

    _setBackgroundInert(on) {
      this._backgroundNodes().forEach(function (node) {
        if (on) {
          node.setAttribute('inert', '');
        } else {
          node.removeAttribute('inert');
        }
      });
    }

    _clearBackground() {
      this._setBackgroundInert(false);
    }

    _focusInput() {
      var input = this.input;
      if (!input) return;
      // Defer so the browser has committed layout before focusing.
      window.requestAnimationFrame(function () {
        try { input.focus({ preventScroll: true }); } catch (_) { try { input.focus(); } catch (_) {} }
      });
    }

    /* ---- submit ----------------------------------------------------------- */

    async _onSubmit(e) {
      e.preventDefault();
      var email = (this.input.value || '').trim();
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        this._showError('Please enter a valid email address.');
        this._focusInput();
        return;
      }

      var listId = this.dataset.listId;
      this._hideError();
      this.button.disabled = true;

      // Dev/QA: with no Klaviyo list configured, unlock without a network call
      // so the gate can be tested without pushing test emails to a live list.
      if (!listId) {
        log('no list_id — unlocking without Klaviyo call');
        this._unlock();
        return;
      }

      // Klaviyo's modern Client Subscribe API is CORS-blocked from inside
      // Shopify. The legacy AJAX subscribe endpoint takes a form-encoded body
      // (a "simple" request → no preflight) and returns readable JSON.
      var params = new URLSearchParams();
      params.append('g', listId);
      params.append('email', email);
      if (this.dataset.source) {
        params.append('$fields', '$source');
        params.append('$source', this.dataset.source);
      }

      var res;
      try {
        res = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString()
        });
      } catch (netErr) {
        log('network error', netErr);
        this._showError('Something went wrong. Please try again.');
        this.button.disabled = false;
        return;
      }

      var rawBody = '';
      try { rawBody = await res.text(); } catch (_) {}
      var payload = null;
      try { payload = JSON.parse(rawBody); } catch (_) {}
      log('Klaviyo response', res.status, rawBody);

      var hasErrors = payload && Array.isArray(payload.errors) && payload.errors.length > 0;
      var explicitFailure = payload && payload.success === false;

      if (res.ok && !hasErrors && !explicitFailure) {
        this._unlock();
        return;
      }

      var errMsg = (payload && payload.errors && payload.errors[0]) ||
        (payload && payload.message) ||
        'Something went wrong. Please try again.';
      this._showError(errMsg);
      this.button.disabled = false;
    }

    /* ---- unlock / state --------------------------------------------------- */

    _unlock() {
      var days = parseInt(this.dataset.cookieDays, 10) || 3650;
      this._setCookie(this.cookieName, '1', days);
      this._markUnlocked(true);
    }

    // reconcile <html> classes, drop inert, hide the gate.
    // showSuccess=true briefly shows the success line before fading out.
    _markUnlocked(showSuccess) {
      document.documentElement.classList.remove(ACTIVE);
      document.documentElement.classList.add(UNLOCKED);
      this._clearBackground();

      if (showSuccess) {
        if (this.form) this.form.hidden = true;
        if (this.success) this.success.hidden = false;
        var self = this;
        setTimeout(function () { self.hidden = true; }, 600);
      } else {
        this.hidden = true;
      }
    }

    _showError(msg) {
      if (!this.error) return;
      this.error.textContent = msg;
      this.error.hidden = false;
    }

    _hideError() {
      if (this.error) this.error.hidden = true;
    }

    /* ---- cookies ---------------------------------------------------------- */

    _getCookie(name) {
      var match = document.cookie.split('; ').find(function (c) { return c.indexOf(name + '=') === 0; });
      return match ? decodeURIComponent(match.split('=')[1]) : null;
    }

    _setCookie(name, value, days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      var secure = location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax' + secure;
    }
  }

  if (!customElements.get('email-gate')) {
    customElements.define('email-gate', EmailGate);
  }
})();
